"use client";

import { useEffect, useRef, useState } from "react";

type Tool = "spotlight" | "box" | "arrow" | "text";

type Annotation =
  | { kind: "spotlight"; x: number; y: number; w: number; h: number }
  | { kind: "box"; x: number; y: number; w: number; h: number; color: string }
  | { kind: "arrow"; x1: number; y1: number; x2: number; y2: number; color: string }
  | { kind: "text"; x: number; y: number; text: string; color: string; size: number };

type Props = {
  /** Source image — captured by html2canvas. */
  imageDataUrl: string;
  /** Accent color from the host theme; used as the default annotation color. */
  accent?: string;
  onCancel: () => void;
  onDone: (blob: Blob, dataUrl: string) => void;
};

const TOOL_LABEL: Record<Tool, string> = {
  spotlight: "Spotlight",
  box: "Box",
  arrow: "Arrow",
  text: "Text",
};

// Basic, intentionally small palette. The host's accent is prepended at render.
const BASIC_COLORS = [
  "#ef4444", // red
  "#f59e0b", // amber
  "#3b82f6", // blue
  "#22c55e", // green
  "#ffffff", // white
] as const;

// Outline color for text: dark on light fills, light on dark fills. Keeps text
// readable over any screenshot without forcing a fixed fill color.
function outlineFor(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return "#0a0a0a";
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 0xff;
  const g = (n >> 8) & 0xff;
  const b = n & 0xff;
  // Relative luminance, sRGB approximation.
  const L = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return L > 0.6 ? "#0a0a0a" : "#ffffff";
}

export function ScreenshotEditor({
  imageDataUrl,
  accent = "#e5484d",
  onCancel,
  onDone,
}: Props) {
  const [tool, setTool] = useState<Tool>("spotlight");
  const [activeColor, setActiveColor] = useState<string>(accent);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [drag, setDrag] = useState<{ x: number; y: number } | null>(null);
  const [hover, setHover] = useState<{ x: number; y: number } | null>(null);
  const [imgSize, setImgSize] = useState<{ w: number; h: number } | null>(null);
  const [busy, setBusy] = useState(false);
  // Text-being-typed: stored in image-natural pixel coords.
  const [editingText, setEditingText] = useState<{
    x: number;
    y: number;
    value: string;
  } | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const textInputRef = useRef<HTMLInputElement>(null);

  // When a fresh text input appears, force focus on the next paint —
  // `autoFocus` alone is unreliable inside <foreignObject> on some browsers.
  useEffect(() => {
    if (!editingText) return;
    const id = requestAnimationFrame(() => {
      textInputRef.current?.focus();
      textInputRef.current?.select();
    });
    return () => cancelAnimationFrame(id);
  }, [editingText?.x, editingText?.y]);

  const palette = [accent, ...BASIC_COLORS];

  function textSizeFor(imgW: number) {
    return Math.max(18, Math.round(imgW * 0.025));
  }

  function commitEditingText() {
    if (!editingText) return;
    const value = editingText.value.trim();
    if (value.length > 0 && imgSize) {
      setAnnotations((a) => [
        ...a,
        {
          kind: "text",
          x: editingText.x,
          y: editingText.y,
          text: value,
          color: activeColor,
          size: textSizeFor(imgSize.w),
        },
      ]);
    }
    setEditingText(null);
  }

  // ESC cancels (or first cancels in-progress text). ⌘Z undoes the last annotation.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        if (editingText) {
          setEditingText(null);
        } else {
          onCancel();
        }
      } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z") {
        // Don't hijack undo inside the text input — let the field handle it.
        if (editingText) return;
        e.preventDefault();
        setAnnotations((a) => a.slice(0, -1));
      }
    }
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onCancel, editingText]);

  /**
   * Convert a pointer event into image-NATURAL pixel coordinates. Storing
   * annotations in native pixels (the same space the final flattened canvas
   * uses) means the live SVG preview and the flatten output use one
   * coordinate system — no per-axis scale conversion at flatten time.
   *
   * The image is rendered at CSS dimensions (r.width × r.height) but its
   * intrinsic resolution is (naturalWidth × naturalHeight). We use the ratio
   * of the two to map a click to native pixels.
   */
  function localPoint(
    e: React.PointerEvent | PointerEvent | React.MouseEvent | MouseEvent,
  ) {
    const img = imgRef.current;
    if (!img || !imgSize) return { x: 0, y: 0 };
    const r = img.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) return { x: 0, y: 0 };
    const cx = e.clientX - r.left;
    const cy = e.clientY - r.top;
    return {
      x: (cx / r.width) * imgSize.w,
      y: (cy / r.height) * imgSize.h,
    };
  }

  const rafRef = useRef<number | null>(null);
  const pendingHoverRef = useRef<{ x: number; y: number } | null>(null);

  function flushHover() {
    rafRef.current = null;
    if (pendingHoverRef.current) {
      setHover(pendingHoverRef.current);
      pendingHoverRef.current = null;
    }
  }

  function onPointerDown(e: React.PointerEvent) {
    if (tool === "text") {
      // Text placement is handled via onClick below — onPointerDown
      // doesn't fire reliably in every environment (some browsers,
      // automated tooling, certain a11y setups), but click always does.
      return;
    }
    const p = localPoint(e);
    (e.target as Element).setPointerCapture?.(e.pointerId);
    setDrag(p);
    setHover(p);
  }

  function onCanvasClick(e: React.MouseEvent) {
    if (tool !== "text") return;
    // If they clicked inside an already-open input, ignore (the input's
    // own click handler stopped propagation, but guard defensively).
    if ((e.target as HTMLElement).tagName === "INPUT") return;
    const p = localPoint(e);
    commitEditingText();
    setEditingText({ x: p.x, y: p.y, value: "" });
  }
  function onPointerMove(e: React.PointerEvent) {
    if (!drag) return;
    pendingHoverRef.current = localPoint(e);
    if (rafRef.current === null) {
      rafRef.current = requestAnimationFrame(flushHover);
    }
  }
  function onPointerUp(e: React.PointerEvent) {
    if (!drag) return;
    // Flush any pending rAF so we don't get a stale hover update after release
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      pendingHoverRef.current = null;
    }
    const end = localPoint(e);
    const start = drag;
    setDrag(null);
    setHover(null);
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    // ~3 native pixels — coords are now in native px so the threshold scales
    // proportionally with the image. We don't want a single-click to register
    // as a zero-length annotation.
    const minMove = 3;
    if (Math.hypot(dx, dy) < minMove) return;

    if (tool === "spotlight" || tool === "box") {
      const x = Math.min(start.x, end.x);
      const y = Math.min(start.y, end.y);
      const w = Math.abs(dx);
      const h = Math.abs(dy);
      if (tool === "spotlight") {
        // Only one spotlight at a time — replace any existing one.
        setAnnotations((a) => [
          ...a.filter((an) => an.kind !== "spotlight"),
          { kind: "spotlight", x, y, w, h },
        ]);
      } else {
        setAnnotations((a) => [...a, { kind: "box", x, y, w, h, color: activeColor }]);
      }
    } else if (tool === "arrow") {
      setAnnotations((a) => [
        ...a,
        { kind: "arrow", x1: start.x, y1: start.y, x2: end.x, y2: end.y, color: activeColor },
      ]);
    }
  }

  function flattenToBlob(): Promise<{ blob: Blob; dataUrl: string }> {
    return new Promise((resolve, reject) => {
      const img = imgRef.current;
      if (!img || !imgSize) return reject(new Error("image not ready"));
      const canvas = document.createElement("canvas");
      canvas.width = imgSize.w;
      canvas.height = imgSize.h;
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("canvas ctx unavailable"));

      ctx.drawImage(img, 0, 0, imgSize.w, imgSize.h);

      // Spotlight first — single rect with a cut-out.
      const spotlight = annotations.find((a) => a.kind === "spotlight") as
        | Extract<Annotation, { kind: "spotlight" }>
        | undefined;
      if (spotlight) {
        ctx.save();
        ctx.fillStyle = "rgba(10, 10, 10, 0.58)";
        ctx.beginPath();
        ctx.rect(0, 0, imgSize.w, imgSize.h);
        ctx.rect(spotlight.x + spotlight.w, spotlight.y, -spotlight.w, spotlight.h); // CCW = cut-out
        ctx.fill("evenodd");
        ctx.strokeStyle = "rgba(255,255,255,0.85)";
        ctx.lineWidth = Math.max(2, imgSize.w * 0.0025);
        ctx.strokeRect(spotlight.x, spotlight.y, spotlight.w, spotlight.h);
        ctx.restore();
      }

      // Boxes
      for (const a of annotations) {
        if (a.kind === "box") {
          ctx.save();
          ctx.strokeStyle = a.color;
          ctx.lineWidth = Math.max(3, imgSize.w * 0.0035);
          ctx.lineJoin = "round";
          ctx.strokeRect(a.x, a.y, a.w, a.h);
          ctx.restore();
        }
      }

      // Arrows
      for (const a of annotations) {
        if (a.kind === "arrow") {
          drawArrow(
            ctx,
            a.x1, a.y1, a.x2, a.y2,
            a.color,
            Math.max(3, imgSize.w * 0.0035),
          );
        }
      }

      // Text annotations — stroke first, then fill, so the outline appears
      // *behind* the fill (canvas equivalent of SVG paint-order:stroke).
      for (const a of annotations) {
        if (a.kind === "text") {
          ctx.save();
          ctx.font = `600 ${a.size}px -apple-system, system-ui, "Segoe UI", Roboto, sans-serif`;
          ctx.textBaseline = "top";
          ctx.lineJoin = "round";
          ctx.miterLimit = 2;
          ctx.strokeStyle = outlineFor(a.color);
          ctx.lineWidth = Math.max(2, a.size * 0.18);
          ctx.strokeText(a.text, a.x, a.y);
          ctx.fillStyle = a.color;
          ctx.fillText(a.text, a.x, a.y);
          ctx.restore();
        }
      }

      canvas.toBlob((blob) => {
        if (!blob) return reject(new Error("toBlob failed"));
        resolve({ blob, dataUrl: canvas.toDataURL("image/png") });
      }, "image/png");
    });
  }

  async function handleDone() {
    // Commit any text that's still being typed so it lands in the screenshot.
    commitEditingText();
    setBusy(true);
    try {
      // Defer one tick so the just-committed annotation is in state.
      await new Promise((r) => requestAnimationFrame(() => r(null)));
      const { blob, dataUrl } = await flattenToBlob();
      onDone(blob, dataUrl);
    } catch (err) {
      console.error("[feedback-hub] flatten failed", err);
      setBusy(false);
    }
  }

  // Compute the rect being drawn in-progress.
  const inProgressRect =
    drag && hover && (tool === "spotlight" || tool === "box")
      ? {
          x: Math.min(drag.x, hover.x),
          y: Math.min(drag.y, hover.y),
          w: Math.abs(hover.x - drag.x),
          h: Math.abs(hover.y - drag.y),
        }
      : null;
  const inProgressArrow =
    drag && hover && tool === "arrow"
      ? { x1: drag.x, y1: drag.y, x2: hover.x, y2: hover.y }
      : null;

  const spotlight = annotations.find((a) => a.kind === "spotlight") as
    | Extract<Annotation, { kind: "spotlight" }>
    | undefined;
  const liveSpotlight =
    tool === "spotlight" && inProgressRect ? inProgressRect : spotlight;

  return (
    <div className="fh-editor" role="dialog" aria-modal="true" aria-label="Annotate screenshot">
      <div className="fh-editor-toolbar">
        <div className="fh-editor-tools">
          {(["spotlight", "box", "arrow", "text"] as Tool[]).map((t) => (
            <button
              key={t}
              type="button"
              className="fh-editor-tool"
              aria-pressed={tool === t}
              onClick={() => {
                // Switching away from "text" should commit/discard any pending input.
                if (tool === "text" && t !== "text") commitEditingText();
                setTool(t);
              }}
            >
              <ToolIcon tool={t} />
              {TOOL_LABEL[t]}
            </button>
          ))}
        </div>
        <div
          className="fh-editor-swatches"
          role="radiogroup"
          aria-label="Annotation color"
        >
          {palette.map((c) => (
            <button
              key={c}
              type="button"
              role="radio"
              aria-checked={activeColor.toLowerCase() === c.toLowerCase()}
              aria-label={`Color ${c}`}
              className="fh-editor-swatch"
              data-active={activeColor.toLowerCase() === c.toLowerCase()}
              style={{ background: c }}
              onClick={() => setActiveColor(c)}
            />
          ))}
        </div>
        <div className="fh-editor-actions">
          <button
            type="button"
            className="fh-editor-secondary"
            disabled={annotations.length === 0}
            onClick={() => setAnnotations((a) => a.slice(0, -1))}
            title="Undo (⌘Z)"
          >
            Undo
          </button>
          <button
            type="button"
            className="fh-editor-secondary"
            disabled={annotations.length === 0}
            onClick={() => setAnnotations([])}
          >
            Clear
          </button>
          <button type="button" className="fh-editor-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="fh-editor-done"
            onClick={handleDone}
            disabled={busy}
            style={{ background: accent }}
          >
            {busy ? "Saving…" : "Use screenshot"}
          </button>
        </div>
      </div>

      <div className="fh-editor-stage">
        <div
          ref={containerRef}
          className="fh-editor-canvas"
          style={{ cursor: tool === "text" ? "text" : "crosshair" }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onClick={onCanvasClick}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            ref={imgRef}
            src={imageDataUrl}
            alt=""
            onLoad={(e) => {
              const el = e.currentTarget;
              setImgSize({ w: el.naturalWidth, h: el.naturalHeight });
            }}
            className="fh-editor-img"
            draggable={false}
          />

          {imgSize && (
            <>
              {/* Spotlight overlay */}
              {liveSpotlight && (
                <svg
                  className="fh-editor-overlay"
                  viewBox={`0 0 ${imgSize.w} ${imgSize.h}`}
                  preserveAspectRatio="none"
                  aria-hidden
                >
                  <defs>
                    <mask id="fh-editor-spot-mask">
                      <rect x="0" y="0" width={imgSize.w} height={imgSize.h} fill="white" />
                      <rect
                        x={liveSpotlight.x}
                        y={liveSpotlight.y}
                        width={liveSpotlight.w}
                        height={liveSpotlight.h}
                        fill="black"
                      />
                    </mask>
                  </defs>
                  <rect
                    x="0"
                    y="0"
                    width={imgSize.w}
                    height={imgSize.h}
                    fill="rgba(10,10,10,0.58)"
                    mask="url(#fh-editor-spot-mask)"
                  />
                  <rect
                    x={liveSpotlight.x}
                    y={liveSpotlight.y}
                    width={liveSpotlight.w}
                    height={liveSpotlight.h}
                    fill="none"
                    stroke="rgba(255,255,255,0.85)"
                    strokeWidth="3"
                    vectorEffect="non-scaling-stroke"
                  />
                </svg>
              )}

              {/* Boxes (committed) + in-progress box + arrows */}
              <svg
                className="fh-editor-overlay"
                viewBox={`0 0 ${imgSize.w} ${imgSize.h}`}
                preserveAspectRatio="none"
                aria-hidden
              >
                {annotations.map((a, i) =>
                  a.kind === "box" ? (
                    <rect
                      key={`b${i}`}
                      x={a.x}
                      y={a.y}
                      width={a.w}
                      height={a.h}
                      fill="none"
                      stroke={a.color}
                      strokeWidth="3"
                      vectorEffect="non-scaling-stroke"
                    />
                  ) : null,
                )}
                {tool === "box" && inProgressRect && (
                  <rect
                    x={inProgressRect.x}
                    y={inProgressRect.y}
                    width={inProgressRect.w}
                    height={inProgressRect.h}
                    fill="none"
                    stroke={accent}
                    strokeDasharray="6 4"
                    strokeWidth="2"
                    vectorEffect="non-scaling-stroke"
                  />
                )}
                {annotations.map((a, i) =>
                  a.kind === "arrow" ? (
                    <ArrowSvg
                      key={`a${i}`}
                      x1={a.x1}
                      y1={a.y1}
                      x2={a.x2}
                      y2={a.y2}
                      color={a.color}
                      imgW={imgSize.w}
                    />
                  ) : null,
                )}
                {tool === "arrow" && inProgressArrow && (
                  <ArrowSvg
                    {...inProgressArrow}
                    color={activeColor}
                    dashed
                    imgW={imgSize.w}
                  />
                )}
                {/* Committed text annotations */}
                {annotations.map((a, i) =>
                  a.kind === "text" ? (
                    <text
                      key={`t${i}`}
                      x={a.x}
                      y={a.y + a.size * 0.82}
                      fill={a.color}
                      stroke={outlineFor(a.color)}
                      strokeWidth={Math.max(2, a.size * 0.18)}
                      paintOrder="stroke"
                      fontSize={a.size}
                      fontWeight={600}
                      fontFamily='-apple-system, system-ui, "Segoe UI", Roboto, sans-serif'
                      style={{ whiteSpace: "pre" }}
                    >
                      {a.text}
                    </text>
                  ) : null,
                )}
                {/* In-progress text input, sized in natural pixels so it scales
                    with the image just like the committed annotations do.
                    Clamped horizontally so it never extends off the image. */}
                {editingText && (() => {
                  const fontSize = textSizeFor(imgSize.w);
                  const boxH = Math.round(fontSize * 1.9);
                  const minBoxW = Math.round(fontSize * 9);
                  const x = Math.min(
                    editingText.x,
                    Math.max(0, imgSize.w - minBoxW - 4),
                  );
                  const y = Math.min(
                    Math.max(0, editingText.y),
                    Math.max(0, imgSize.h - boxH - 4),
                  );
                  return (
                    <foreignObject
                      x={x}
                      y={y}
                      width={Math.max(minBoxW, imgSize.w - x - 4)}
                      height={boxH + 8}
                      style={{ overflow: "visible", pointerEvents: "auto" }}
                    >
                      <input
                        ref={textInputRef}
                        autoFocus
                        value={editingText.value}
                        placeholder="Type a label, Enter to add"
                        onChange={(e) =>
                          setEditingText((s) =>
                            s ? { ...s, value: e.target.value } : s,
                          )
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            commitEditingText();
                          } else if (e.key === "Escape") {
                            e.preventDefault();
                            e.stopPropagation();
                            setEditingText(null);
                          }
                        }}
                        onBlur={commitEditingText}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          font: `600 ${fontSize}px -apple-system, system-ui, "Segoe UI", Roboto, sans-serif`,
                          color: "#0a0a0a",
                          background: "#ffffff",
                          border: `2px solid ${activeColor}`,
                          boxShadow: `0 0 0 4px ${activeColor}33, 0 6px 18px rgba(0,0,0,0.35)`,
                          borderRadius: 6,
                          padding: `${Math.round(fontSize * 0.2)}px ${Math.round(fontSize * 0.4)}px`,
                          outline: "none",
                          width: "auto",
                          minWidth: minBoxW,
                          caretColor: activeColor,
                          // The placeholder uses the browser default which
                          // is too faint over white at this scale — bumping
                          // via the global selector inside a foreignObject
                          // isn't reliable, so we accept the default fade.
                        }}
                      />
                    </foreignObject>
                  );
                })()}
              </svg>
            </>
          )}
        </div>
      </div>

      <div className="fh-editor-help">
        {tool === "spotlight"
          ? "Drag to focus an area. Only one spotlight per screenshot."
          : tool === "box"
            ? "Drag a rectangle to circle something."
            : tool === "arrow"
              ? "Drag to draw an arrow pointing at it."
              : "Click to place a label, then type. Enter to commit."}{" "}
        <span className="fh-editor-help-keys">
          <kbd>⌘Z</kbd> undo · <kbd>Esc</kbd> cancel
        </span>
      </div>
    </div>
  );
}

/* ──────────────────────── SVG helpers ──────────────────────── */

function ArrowSvg({
  x1,
  y1,
  x2,
  y2,
  color,
  dashed,
  imgW,
}: {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color: string;
  dashed?: boolean;
  imgW: number;
}) {
  // Coords are in IMAGE NATURAL PIXELS. Arrow head size scales with the image.
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  // ~1.8% of image width, with a minimum so tiny arrows still have a head.
  const headSize = Math.max(14, imgW * 0.018);
  const baseX = x2 - ux * headSize;
  const baseY = y2 - uy * headSize;
  const perpX = -uy * headSize * 0.55;
  const perpY = ux * headSize * 0.55;

  return (
    <g>
      <line
        x1={x1}
        y1={y1}
        x2={baseX}
        y2={baseY}
        stroke={color}
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray={dashed ? "6 4" : undefined}
        vectorEffect="non-scaling-stroke"
      />
      <polygon
        points={`${x2},${y2} ${baseX + perpX},${baseY + perpY} ${baseX - perpX},${baseY - perpY}`}
        fill={color}
        stroke="none"
      />
    </g>
  );
}

function ToolIcon({ tool }: { tool: Tool }) {
  if (tool === "spotlight") {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
        <rect x="4" y="6" width="16" height="12" stroke="currentColor" strokeWidth="1.5" rx="1.5" />
        <rect x="8" y="9" width="8" height="6" stroke="currentColor" strokeWidth="1.5" fill="rgba(255,255,255,0.95)" />
      </svg>
    );
  }
  if (tool === "box") {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
        <rect x="4" y="5" width="16" height="14" stroke="currentColor" strokeWidth="1.7" rx="1.5" />
      </svg>
    );
  }
  if (tool === "arrow") {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path d="M5 19 L19 5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        <path d="M19 5 L13 5 M19 5 L19 11" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  // text
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M6 6 H18" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <path d="M12 6 V19" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <path d="M9 19 H15" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

/* ──────────────────────── Canvas drawing ──────────────────────── */

function drawArrow(
  ctx: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  color: string,
  width: number,
) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const headSize = Math.max(12, len * 0.08);
  const baseX = x2 - ux * headSize;
  const baseY = y2 - uy * headSize;

  ctx.save();
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = width;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(baseX, baseY);
  ctx.stroke();

  const perpX = -uy * headSize * 0.55;
  const perpY = ux * headSize * 0.55;
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(baseX + perpX, baseY + perpY);
  ctx.lineTo(baseX - perpX, baseY - perpY);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}
